import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../util/Auth";
import { getMyShifts } from "../../api/ShiftsApi";
import getMe from "../../api/UsersApi";
import { MyShift } from "../../models/Plantao";
import { formatDateTime } from "../../util/timezone.service";
import useTimezone from "../../hooks/useTimezone";

type MeuHorarioRow = {
  id: string;
  place: string;
  dateRange: string;
  role: string;
  link?: string;
  isOnline: boolean;
  facilitator: MyShift["facilitator"];
};

type UseMeusHorariosResult = {
  horarios: MeuHorarioRow[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
};

export default function useMeusHorarios(): UseMeusHorariosResult {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { timezone } = useTimezone();
  const [shifts, setShifts] = useState<MyShift[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchMyShifts = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      let resolvedUserId = user?.id?.trim();

      if (!resolvedUserId) {
        const me = await getMe();
        resolvedUserId = (me as any)?.id?.toString()?.trim();
      }

      if (!resolvedUserId) {
        setShifts([]);
        setError("Não foi possível identificar o usuário.");
        return;
      }

      const data = await getMyShifts(resolvedUserId);
      setShifts(data);
    } catch {
      setError("Não foi possível carregar seus horários.");
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchMyShifts().catch((err) => console.error("Erro ao carregar horários:", err));
  }, [fetchMyShifts]);

  const datePattern = t("pages.configuration.dateTimeFormats.default");
  const locale = t("current_locale");

  const sortedShifts = [...shifts].sort((a, b) => a.startDate.getTime() - b.startDate.getTime());

  const horarios: MeuHorarioRow[] = sortedShifts.map((s) => {
    const startFormatted = formatDateTime(s.startDate, datePattern, { timezone, locale });
    const endFormatted = s.endDate
      ? formatDateTime(s.endDate, datePattern, { timezone, locale })
      : null;
    const dateRange = endFormatted ? `${startFormatted} - ${endFormatted}` : startFormatted;

    const roleKey =
      s.role === "facilitador"
        ? "roles.facilitator"
        : s.role === "entrevistador"
        ? "roles.interviewer"
        : "roles.assistant";

    return {
      id: s.id,
      place: s.place,
      dateRange,
      role: t(roleKey),
      link: s.link,
      isOnline: s.mode.toUpperCase() === "ONLINE",
      facilitator: s.facilitator
    };
  });

  return { horarios, isLoading, error, reload: fetchMyShifts };
}
